import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useApplicationForm from './useApplicationForm';
import { fontSizeLarge, spacing40, spacing80 } from '@ellucian/react-design-system/core/styles/tokens';
import { Alert, makeStyles } from '@ellucian/react-design-system/core';
import debounce from 'lodash/debounce';
import { IntlProvider } from 'react-intl';
import { Typography } from '@ellucian/react-design-system/core';
import { WatchValues } from '@nsr/react-form';
import ApplicationForm from './ApplicationForm';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
import useMaintenanceStatus from '../Helpers/useMaintenanceStatus';
import { getMaintenanceMessages } from '../Helpers/maintenanceMessageHelpers';
import { CONSTANTS } from '../Helpers/Configuration';
const autosaveInterval = 500;

const useStyles = makeStyles({
    name: 'ApplicationFormLayout'
})({
    recaptchaBranding: {
        textAlign: 'center',
        margin: `0 ${spacing40} 0 ${spacing40}`,
    },
    maintenanceBanner: {
        margin: spacing40,
    },
    maintenancePage: {
        alignItems: 'center',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: '48rem',
        minHeight: 'calc(100vh - 8rem)',
        margin: '0 auto',
        padding: `${spacing80} ${spacing40}`,
        textAlign: 'center',
    },
    maintenanceMessage: {
        fontSize: fontSizeLarge,
        lineHeight: 1.6,
    },
    maintenanceSchedule: {
        marginTop: spacing40,
    },
});
const ApplicationFormLayout = (props) => {
    const { isAps } = props;
    const intl = useIntl();
    const ctx = useApplicationForm();
    const { formMessages, adminTenant } = ctx;
    const { classes } = useStyles();
    const callAutosave = useCallback(
        debounce(ctx.autosave, autosaveInterval),
        [ctx.autosave]
    );
 
    const isFormReady = ctx.template !== null && formMessages !== null;
    const globalSettings = window.PRELOADED_STATE?.globalSettings || adminTenant || {};
    const { recaptcha } = globalSettings;
    const { maintenanceStatus, status } = useMaintenanceStatus(
        globalSettings.maintenanceStatus,
        !ctx.previewMode && isFormReady
    );
    const {
        maintenanceStartDateTime,
        maintenanceEndDateTime,
        maintenanceMessage,
        warningBannerMessage,
    } = getMaintenanceMessages(intl, maintenanceStatus);

    if (!isFormReady) {
        return null;
    }

    return (
        <IntlProvider locale={intl.locale} messages={formMessages}>
            <>
                {status === CONSTANTS.maintenanceStatus.WARNING_WINDOW && warningBannerMessage &&
                    <div className={classes.maintenanceBanner}>
                        <Alert alertType="warning" open={true} text={warningBannerMessage} userDismissable={false} />
                    </div>
                }
                {status === CONSTANTS.maintenanceStatus.ACTIVE ?
                    <MaintenancePage
                        startDateTime={maintenanceStartDateTime}
                        endDateTime={maintenanceEndDateTime}
                        message={maintenanceMessage}
                    /> :
                    <>
                        { !isAps &&
                            <WatchValues onValuesChange={callAutosave} />
                        }
                        <ApplicationForm />
                        {
                            recaptcha?.recaptchaToggle && <RecaptchaBranding/>
                        }
                    </>
                }
                {/* <ReduxStateDump /> */}
            </>
        </IntlProvider>
    );
};

const MaintenancePage = ({ message }) => {
    const { classes } = useStyles();

    return (
        <main className={classes.maintenancePage}>
            <Typography variant="h2"><FormattedMessage id="maintenancePageTitle" /></Typography>
            {message && <div className={classes.maintenanceMessage}><HtmlFormattedMessage htmlString={message} normalizeSpaces /></div>}
        </main>
    );
};

const RecaptchaBranding = () => {
    const { classes } = useStyles();

    return (
        <aside aria-label='Recaptcha Branding' >
            <Typography className={classes.recaptchaBranding}>
                <HtmlFormattedMessage descriptor={{ id: 'recaptchaBranding' }} />
            </Typography>
        </aside>
    );
};

export default ApplicationFormLayout;
